import { createContext, useState, useContext, useEffect, type ReactNode, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { getThumbmark } from '@thumbmarkjs/thumbmarkjs';
import { UserRole } from '../types/UserRole';
import type { UserPermission } from '../types/UserPermission';

// ###################################################################
// 型定義
// ###################################################################

// アプリケーション内で扱うユーザー情報の型
export interface AuthenticatedUser {
  loginId: string;
  name: string;
  role: UserRole;
  masterCode: string;
  parentCode: number;
  childCode: number;
  permission: number;
}

// JWTの中身（クレーム）の型定義
// C#側でセットしたClaim名に合わせます
interface JwtPayload {
  sub: string;         // ユーザーID (Guid)
  unique_name: string; // ログインID (username)
  name: string;        // ユーザー名
  role: string;        // 役割
  permission: number;  // 権限
  master_code: string; // マスタコード (文字列で入ってくることが多い)
  exp: number;         // 有効期限
}

// Contextが提供する機能の型
interface AuthContextType {
  currentUser: AuthenticatedUser | null;
  login: (loginId: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  accessToken: string | null; // APIコール時に使用するトークン
  isLoading: boolean;
  isNewPasswordRequired: boolean;
  updateAccessToken: (token: string) => void;
  completeNewPassword: (newPassword: string) => void;
}

// ###################################################################
// Context生成
// ###################################################################
const AuthContext = createContext<AuthContextType | null>(null);

// ###################################################################
// Provider実装
// ###################################################################
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<AuthenticatedUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isNewPasswordRequired, setIsNewPasswordRequired] = useState(false);
  // 一時的なセッショントークンを保持するstate
  const [tempSessionToken, setTempSessionToken] = useState<string | null>(null);

  // 環境変数からAPIのベースURLを取得
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

  // ----------------------------------------------------------------
  // ヘルパー: アクセストークンからユーザー情報を抽出してStateにセット
  // ----------------------------------------------------------------
  const setSessionFromToken = useCallback((token: string) => {
    try {
      const decoded = jwtDecode<JwtPayload>(token);

      const [pCode, cCode] = decoded.master_code.split('_').map(Number);
      
      const user: AuthenticatedUser = {
        loginId: decoded.unique_name,
        name: decoded.name,
        // 型アサーションでroleを絞り込む（必要に応じてバリデーション）
        role: decoded.role as UserRole,
        masterCode: decoded.master_code,
        parentCode: pCode,
        childCode: cCode,
        permission: decoded.permission as UserPermission,
      };

      setAccessToken(token);
      setCurrentUser(user);
      return true;
    } catch (e) {
      console.error("Token decode failed", e);
      return false;
    }
  }, []);

  // ----------------------------------------------------------------
  // 新しいトークンが取れた際にContextの状態を更新する
  // ----------------------------------------------------------------
  const updateAccessToken = useCallback((token: string) => {
    setSessionFromToken(token);
  }, [setSessionFromToken]);

  // ----------------------------------------------------------------
  // F5対応: アプリ起動時(マウント時)にリフレッシュを試みてログイン状態を復元
  // ----------------------------------------------------------------
  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);

      try {
        const response = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          {}, // POST bodyは空
          { 
            withCredentials: true, // fetchの credentials: 'include' と同じ意味
            headers: { 'Content-Type': 'application/json' }
          }
        );

        // Axiosはステータス200系以外だとcatchに飛ぶので、if(response.ok)チェックは不要
        const token = response.data.newAccessToken;

        if (token) {
          setSessionFromToken(token);
        } else {
          // 成功したけどトークンがない謎の状態
          throw new Error("No access token in refresh response");
        }

      } catch (error) {
        // 401エラーやネットワークエラーの場合
        console.warn("Silent refresh failed or expired.", error);
        
        // リフレッシュ失敗＝完全にログアウト状態とする
        setAccessToken(null);
        setCurrentUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [API_BASE_URL, setSessionFromToken]);
  
  // ----------------------------------------------------------------
  // ログイン機能
  // ----------------------------------------------------------------
  const login = useCallback(async (loginId: string, password: string) => {
    try {

      // フィンガープリントを取得（非同期）
      // includeを指定しないと12秒ほどかかるため
      const fp = await getThumbmark({
        include: [
          'app_codename', 'app_name', 'app_version', 'architecture', 'audio','available_fonts', 
          'brave', 'browser', 'build_id', 'canvas','client_rects', 
          //'connection', //通信速度によって変わるので除外
          'cookies', 'device_memory', 'fonts', 'hardware', 'hardware_concurrency', 'languages', 
          'oscpu', 'permissions', 'plugins', 'product', 'product_sub', 'screen', 'system', 
          'touch', 'vendor', 'video', 'webdriver', 'webgl','webgl_vendor', 
        ]
      });

      const fpString = typeof fp === 'string' ? fp : (fp as any).thumbmark;

      const response = await axios.post(
        `${API_BASE_URL}/auth/login`,
        { 
          loginId, 
          password, 
          fingerprint: fpString
        },
        {
          withCredentials: true, // credentials: 'include' の代わり
          headers: { 'Content-Type': 'application/json' },
        }
      );

      const data = response.data;
      
      // パスワード変更要求の場合
      if (response.status === 202 && data.isPasswordChangeRequired) {
          setTempSessionToken(data.sessionToken);
          setIsNewPasswordRequired(true);
          return;
      }

      // data.accessToken を受け取って状態を更新
      if (data.accessToken) {
        setSessionFromToken(data.accessToken);
      }
      } catch (error) {
        console.error("Login error:", error);
        if (axios.isAxiosError(error) && error.response) {
          // ステータスコードが 423 (Locked) の場合
          if (error.response.status === 423) {
            const lockedMessage = error.response.data?.message || "失敗回数が規定値を超えたため、アカウントをロックしました。";
            throw new Error(lockedMessage);
          }

          const message = error.response.data?.message || 'Login failed';
          throw new Error(message);
        }
        
        // ネットワークエラーなど
        throw error;
      }
  }, [API_BASE_URL, setSessionFromToken]);

  // ----------------------------------------------------------------
  // パスワード変更機能
  // ----------------------------------------------------------------
  const completeNewPassword = useCallback(async (newPassword: string) => {
      if (!tempSessionToken) throw new Error("Session expired");

      try {
        const response = await axios.post(
          `${API_BASE_URL}/auth/change-password`,
          { 
            sessionToken: tempSessionToken, 
            newPassword: newPassword 
          },
          {
            withCredentials: true,
            headers: { 'Content-Type': 'application/json' },
          }
        );

        const token = response.data.accessToken;
        if (token) {
          setSessionFromToken(token);
          setIsNewPasswordRequired(false);
          setTempSessionToken(null);
        } else {
          throw new Error("No access token returned after password change");
        }      
      } catch (error: any) {
        console.error("Password change error:", error);
        if (axios.isAxiosError(error) && error.response) {
          throw new Error(error.response.data?.message || 'Failed to change password');
        }
        throw new Error("Failed");
      }
    }, [tempSessionToken, setSessionFromToken]);

  // ----------------------------------------------------------------
  // ログアウト機能
  // ----------------------------------------------------------------
  const logout = useCallback(async () => {
    try {
      // サーバー側のクッキー削除とDB無効化を呼び出す
      // 第2引数はbody(データ)なので、空オブジェクト {} を渡す必要があります
      await axios.post(
        `${API_BASE_URL}/auth/logout`,
        {}, // 空のbody
        {
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' },
        }
      );      
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // 成功失敗にかかわらず、フロント側の状態をクリア
      setAccessToken(null);
      setCurrentUser(null);
    }
  }, [API_BASE_URL]);

  // ----------------------------------------------------------------
  // Providerの値を生成
  // ----------------------------------------------------------------
  const value = {
    currentUser,
    login,
    logout,
    accessToken,
    isLoading,
    isNewPasswordRequired,
    updateAccessToken,
    completeNewPassword
  };

  if (isLoading) {
    // 初期化中（リフレッシュトークン確認中）は画面を描画しない、またはローディング表示
    return <div>Loading authentication...</div>;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ###################################################################
// useAuthフック
// ###################################################################
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};